<template>
  <v-sheet flat border class="pa-4 col-sm-12">
    <p class="text-subtitle-1 pt-3 text-primary mb-3">
      <span class="font-weight-bold">Rooftop Unit (v2)</span>
      - Input values at execution time
    </p>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CurrentDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Site Current Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.ReadingDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Reading Date/Time"
          type="datetime-local"
          hint="The latest hour of the rooftop unit; the other 23 hours count back from it."
          persistent-hint
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.DelayInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Delay in Minutes"
        />
      </v-col>
    </v-row>
    <BusinessHoursInput
      v-model:start-time="localInputData.SiteStartTime"
      v-model:end-time="localInputData.SiteEndTime"
    />
    <v-row>
        <v-col sm="4">
          <v-text-field
            v-model="localInputData.Temperature"
            :rules="[isDecimalOrBlank]"
            variant="outlined"
            density="compact"
            label="Outside Temperature"
          />
        </v-col>
    </v-row>
    <v-row>
      <v-col sm="8">
        <v-select
          v-model="localInputData.EndUseId"
          :items="endUseOptions"
          :item-title="(item) => item.Name || ''"
          :item-value="(item) => item.Id"
          :rules="[hasValue]"
          label="RTU Name"
          variant="outlined"
          density="compact"
          data-testid="rtu-end-use"
        />
      </v-col>
    </v-row>

    <v-divider class="my-4" />

    <v-row align="center" dense class="mb-1">
      <v-col cols="12" sm="6">
        <p class="text-subtitle-2 text-primary font-weight-bold mb-0">
          Rooftop Unit Hourly Statuses
        </p>
      </v-col>
      <v-col cols="12" sm="6" class="d-flex justify-end align-center ga-2">
        <v-btn
          variant="text"
          density="comfortable"
          prepend-icon="mdi-arrow-down-bold-outline"
          data-testid="rtu-copy-latest-hour"
          @click="copyLatestHourDown"
        >
          Copy latest hour down
        </v-btn>
        <v-btn
          variant="text"
          density="comfortable"
          prepend-icon="mdi-eraser"
          data-testid="rtu-clear-hours"
          @click="clearHours"
        >
          Clear
        </v-btn>
      </v-col>
    </v-row>

    <p
      v-if="!endUseOptions.length"
      class="text-body-2 text-medium-emphasis mb-3"
      data-testid="rtu-end-uses-empty"
    >
      No RTU End Uses are available for this site.
    </p>

    <v-table density="compact" fixed-header height="440" class="mt-2">
      <thead>
        <tr>
          <th class="text-left">Hour</th>
          <th class="text-left">kW</th>
          <th class="text-left">50% Swings</th>
          <th class="text-left">Runtime (min)</th>
          <th class="text-left">Starts</th>
          <th class="text-left">Shutdowns</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="hour in hourRows"
          :key="hour.offset"
          data-testid="rtu-hour-row"
        >
          <td class="text-no-wrap">
            <span data-testid="rtu-hour-label">{{ hour.Label }}</span>
            <span
              v-if="hour.offset === 0"
              class="text-caption text-medium-emphasis ml-1"
            >
              (latest)
            </span>
          </td>
          <td>
            <v-text-field
              v-model="hours[hour.offset].kw"
              :rules="[isDecimalOrBlank]"
              variant="outlined"
              density="compact"
              hide-details="auto"
              data-testid="rtu-kw"
            />
          </td>
          <td>
            <v-text-field
              v-model="hours[hour.offset].swingCount"
              :rules="[isCountOrBlank]"
              variant="outlined"
              density="compact"
              hide-details="auto"
              data-testid="rtu-swing-count"
            />
          </td>
          <td>
            <v-text-field
              v-model="hours[hour.offset].runtimeInMinutes"
              :rules="[isCountOrBlank]"
              variant="outlined"
              density="compact"
              hide-details="auto"
              data-testid="rtu-runtime"
            />
          </td>
          <td>
            <v-text-field
              v-model="hours[hour.offset].startCount"
              :rules="[isCountOrBlank]"
              variant="outlined"
              density="compact"
              hide-details="auto"
              data-testid="rtu-start-count"
            />
          </td>
          <td>
            <v-text-field
              v-model="hours[hour.offset].shutdownCount"
              :rules="[isCountOrBlank]"
              variant="outlined"
              density="compact"
              hide-details="auto"
              data-testid="rtu-shutdown-count"
            />
          </td>
        </tr>
      </tbody>
    </v-table>
  </v-sheet>
</template>
<script>
import {
  isNumber,
  isNumberGreaterThanZero,
  hasValue,
  isDate,
} from '@/Core.Patterns/Factory/validationFactory.ts';
import BusinessHoursInput from '@/Core.Service.Domain/Strategies/Strategies/Components/BusinessHoursInput.vue';
import { withBaseInputDefaults } from '@/Core.Service.Domain/Strategies/Strategies/Utils/baseInputDefaults.ts';

// An instance of RooftopUnitV2 holds a single RTU End Use, so this editor is one
// grid rather than V1's list of panels. The End Use is still picked by hand --
// a what-if run has to say which RTU it is standing in for, since EndUseName is
// a Field the rule can read and it is what HumanFriendlyIdentifier is built from.
//
// NOTE: RooftopUnitV2.GetDataSourceNames() publishes Contacts only -- it drops
// EndUsesRtu on the grounds that an input holding one End Use has nothing to
// pick with, and RooftopUnitV2Tests asserts the absence. Until that method
// publishes EndUsesRtu again the list below arrives empty and the pick list is
// limited to whatever a replayed run recorded.
const END_USE_DATA_SOURCE = 'EndUsesRtu';

// The input looks back 24 hours -- TotalRuntimeFor24Hours and friends -- so the
// grid offers exactly the window the rule methods can reach. Row 0 is the
// latest hour, the one the latest-hour methods read; row 23 is 23 hours before it.
const HOURS_IN_A_DAY = 24;

// One entry per numeric column of RtuHourlyStatus. `Key` is the row field,
// `Property` the name the record deserialises from, and `Whole` marks the ones
// typed as int on the Core side -- those have to travel as integers or
// Newtonsoft refuses to build the record.
const HOURLY_VALUES = [
  { Key: 'kw', Property: 'ReadingValueKw', Whole: false },
  { Key: 'swingCount', Property: 'SwingCount', Whole: true },
  { Key: 'runtimeInMinutes', Property: 'RuntimeInMinutes', Whole: true },
  { Key: 'startCount', Property: 'StartCount', Whole: true },
  { Key: 'shutdownCount', Property: 'ShutdownCount', Whole: true },
];

export default {
  name: 'RooftopUnitV2Input',

  components: { BusinessHoursInput },

  props: {
    InputData: {
      type: Object,
      required: true,
    },
    DataSources: {
      type: Array,
      required: true,
    },
  },

  data() {
    const localInputData = withBaseInputDefaults(this.InputData);
    const recorded = Array.isArray(this.InputData?.HourlyStatuses)
      ? this.InputData.HourlyStatuses
      : [];

    // A replayed run carries the End Use on the input itself, but the rows
    // recorded against it name it too, so a run logged before EndUseId was
    // filled in still opens on the right RTU.
    const [firstRecorded] = recorded;
    if (!hasValue(localInputData.EndUseId)) {
      localInputData.EndUseId = hasValue(firstRecorded?.EndUseId)
        ? Number(firstRecorded.EndUseId)
        : null;
    }
    if (!hasValue(localInputData.EndUseName)) {
      localInputData.EndUseName = firstRecorded?.EndUseName ?? '';
    }

    const anchor = toAnchorHour(localInputData.ReadingDateTime);
    const hours = newHours();

    // The hours a recorded run holds are whatever the stored procedure looked
    // back on (24 at the time of writing), and they land on the offset their
    // timestamp works out to. Anything outside the window rides along untouched
    // in `extraStatuses` rather than being quietly dropped.
    const extraStatuses = [];
    recorded.forEach((status) => {
      const offset = toHourOffset(status?.ReadingDateTime, anchor);
      if (offset === null) {
        extraStatuses.push(status);
        return;
      }

      const hour = hours[offset];
      HOURLY_VALUES.forEach(({ Key, Property }) => {
        hour[Key] = hasValue(status[Property]) ? String(status[Property]) : '';
      });
      hour.temperature = hasValue(status.Temperature)
        ? Number(status.Temperature)
        : null;
    });

    return {
      localInputData,
      hours,
      extraStatuses,
    };
  },

  computed: {
    // The RTU End Uses the site exposes. Items carry only { Id, Name }.
    endUses() {
      return (
        this.DataSources?.find((ds) => ds.Key === END_USE_DATA_SOURCE)?.Value ??
        []
      ).map((endUse) => ({ Id: endUse.Id, Name: endUse.Name }));
    },

    // An End Use the site no longer exposes -- or one the site never published,
    // because V2 does not ask for the EndUsesRtu list -- still has to be shown
    // when replaying a logged execution. Unlike V1, this input records the name
    // alongside its values, so the RTU keeps its name rather than its bare id.
    endUseOptions() {
      const selectedId = this.localInputData.EndUseId;
      if (
        !hasValue(selectedId) ||
        this.endUses.some((endUse) => endUse.Id === selectedId)
      ) {
        return [...this.endUses].sort(byName);
      }

      const recordedName = this.localInputData.EndUseName;
      return [
        ...this.endUses,
        {
          Id: selectedId,
          Name: hasValue(recordedName)
            ? recordedName
            : `Rooftop Unit #${selectedId}`,
        },
      ].sort(byName);
    },

    selectedEndUse() {
      return this.endUseOptions.find(
        (endUse) => endUse.Id === this.localInputData.EndUseId
      );
    },

    // The hour every row stands for, counted back from Reading Date/Time --
    // which is the hour the input treats as its latest. Editing that field
    // re-stamps the whole grid, values and all, because a row is held by its
    // offset rather than by its timestamp.
    hourRows() {
      const anchor = toAnchorHour(this.localInputData?.ReadingDateTime);

      return Array.from({ length: HOURS_IN_A_DAY }, (_, offset) => {
        if (anchor === null) {
          return { offset, Stamp: null, Label: `-${offset} h` };
        }

        const hour = new Date(anchor.getTime());
        hour.setHours(hour.getHours() - offset);
        return {
          offset,
          Stamp: toHourStamp(hour),
          Label: toHourLabel(hour, anchor),
        };
      });
    },
  },

  watch: {
    localInputData: {
      handler() {
        this.emitModel();
      },
      deep: true,
    },

    hours: {
      handler() {
        this.emitModel();
      },
      deep: true,
    },
  },

  methods: {
    isNumber,
    isNumberGreaterThanZero,
    hasValue,
    isDate,

    // Every grid field may be left blank -- a blank hour is sent as 0, the same
    // value the input would read for an hour it has no row for -- so the shared
    // isNumber rule, which counts blank as invalid, would paint the whole grid
    // red before a single value was typed.
    isDecimalOrBlank(value) {
      return !hasValue(value) || isNumber(value);
    },

    // Swings, runtime minutes, starts and shutdowns are int on RtuHourlyStatus.
    isCountOrBlank(value) {
      return (
        !hasValue(value) ||
        (isNumber(value) &&
          Number.isInteger(Number(value)) &&
          Number(value) >= 0)
      );
    },

    // 24 hours x 5 values is 120 fields, and a test run usually wants the same
    // reading repeated across the window (an RTU that never shut down, a unit
    // stuck at one kW). The latest hour is the one the latest-hour methods
    // read, so it is the one worth spreading.
    copyLatestHourDown() {
      const [latest] = this.hours;
      this.hours.forEach((hour, offset) => {
        if (offset === 0) return;

        HOURLY_VALUES.forEach(({ Key }) => {
          hour[Key] = latest[Key];
        });
      });
    },

    clearHours() {
      this.hours.forEach((hour) => {
        HOURLY_VALUES.forEach(({ Key }) => {
          hour[Key] = '';
        });
      });
    },

    emitModel() {
      // Unlike the End Use editors, which send only the readings that were
      // typed, this one sends all 24 hours: the grid is the payload. A blank
      // field is a 0, which is what the input reads for a missing hour anyway,
      // and keeping the rows whole means the "latest hour" the methods read is
      // always the top row rather than the topmost row that happened to be
      // filled in.
      //
      // The grid is sent whether or not an End Use has been picked. The v2
      // methods read HourlyStatuses directly rather than looking an End Use up,
      // so the values are worth running even before the RTU is named -- and
      // with EndUsesRtu unpublished there may be nothing to pick from. The
      // field's hasValue rule is what asks for the pick.
      const hourly = this.hourRows
        // No Stamp means Reading Date/Time is mid-edit and unparseable. The run
        // is broken until it is valid again, and the values stay in the grid,
        // so there is nothing worth stamping with a guess.
        .filter((hour) => hour.Stamp !== null)
        .map((hour) => this.toHourlyStatus(hour));

      const endUseId = this.selectedEndUse?.Id ?? this.localInputData.EndUseId;
      const endUseName =
        this.selectedEndUse?.Name ?? this.localInputData.EndUseName ?? '';

      this.$emit('update:model', {
        ...this.localInputData,
        EndUseId: endUseId,
        EndUseName: endUseName,
        // An instance of this input holds one RTU, so rows that fell outside
        // the grid's window are re-stamped with the End Use the input now
        // names; leaving them on their recorded id would hand the rule a
        // HourlyStatuses spanning two RTUs.
        HourlyStatuses: [
          ...hourly,
          ...this.extraStatuses.map((status) => ({
            ...status,
            EndUseId: endUseId,
            EndUseName: endUseName,
          })),
        ],
      });
    },

    toHourlyStatus(hour) {
      const values = this.hours[hour.offset];

      return HOURLY_VALUES.reduce(
        (status, { Key, Property, Whole }) => {
          status[Property] = Whole
            ? toCount(values[Key])
            : (toDecimal(values[Key]) ?? 0);
          return status;
        },
        {
          ReadingDateTime: hour.Stamp,
          EndUseId: this.selectedEndUse?.Id ?? this.localInputData.EndUseId,
          EndUseName:
            this.selectedEndUse?.Name ?? this.localInputData.EndUseName ?? '',
          // Only the most recent hour's temperature is ever read (it is what
          // fills the input's own Outside Temperature), but a replayed run
          // arrives with one per row, so a recorded hour keeps the temperature
          // it ran with and a hand-entered one takes the field above.
          Temperature:
            values.temperature ?? toDecimal(this.localInputData.Temperature),
        }
      );
    },
  },
};

// The grid always shows all 24 hours, blank ones included.
function newHours() {
  return Array.from({ length: HOURS_IN_A_DAY }, () =>
    HOURLY_VALUES.reduce((hour, { Key }) => ({ ...hour, [Key]: '' }), {
      temperature: null,
    })
  );
}

// The fields take free text, so guard what leaves as a reading. Number() is
// used rather than parseFloat() because parseFloat("12abc") silently yields 12
// -- the field would flag it via its rule while the payload carried it anyway.
// Infinity (and "1e999", which overflows to it) is rejected too: the backend
// stores these as decimal.
function toDecimal(value) {
  if (!hasValue(value) || String(value).trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// SwingCount, RuntimeInMinutes, StartCount and ShutdownCount are int, and
// Newtonsoft will not build RtuHourlyStatus out of a fractional number, so a
// value that slipped past the field rule is rounded rather than shipped as is.
function toCount(value) {
  const parsed = toDecimal(value);
  return parsed === null ? 0 : Math.round(parsed);
}

// `datetime-local` hands back local wall-clock time, and so does a recorded
// run, so both parse as local. The hour is the unit the input works in -- the
// stored procedure returns one row per hour -- so the minutes are dropped.
function toAnchorHour(readingDateTime) {
  if (!hasValue(readingDateTime)) {
    return null;
  }

  const anchor = new Date(readingDateTime);
  if (Number.isNaN(anchor.getTime())) {
    return null;
  }

  anchor.setMinutes(0, 0, 0);
  return anchor;
}

// Which grid row a recorded status belongs on, or null when it falls outside
// the 24-hour window the grid covers.
function toHourOffset(readingDateTime, anchor) {
  if (anchor === null || !hasValue(readingDateTime)) {
    return null;
  }

  const hour = new Date(readingDateTime);
  if (Number.isNaN(hour.getTime())) {
    return null;
  }

  const offset = Math.round(
    (anchor.getTime() - hour.getTime()) / (60 * 60 * 1000)
  );
  return offset >= 0 && offset < HOURS_IN_A_DAY ? offset : null;
}

const pad = (part) => String(part).padStart(2, '0');

// RtuHourlyStatus.ReadingDateTime is a DateTime, so it travels as local
// wall-clock time -- the same shape the base input's timestamps use.
function toHourStamp(hour) {
  return (
    `${hour.getFullYear()}-${pad(hour.getMonth() + 1)}-${pad(hour.getDate())}` +
    `T${pad(hour.getHours())}:00:00`
  );
}

// The window walks back over midnight, so the hours before it carry their date.
function toHourLabel(hour, anchor) {
  const sameDay =
    hour.getFullYear() === anchor.getFullYear() &&
    hour.getMonth() === anchor.getMonth() &&
    hour.getDate() === anchor.getDate();

  return sameDay
    ? `${pad(hour.getHours())}:00`
    : `${pad(hour.getMonth() + 1)}/${pad(hour.getDate())} ${pad(hour.getHours())}:00`;
}

function byName(a, b) {
  return (a.Name ?? '').localeCompare(b.Name ?? '');
}
</script>
